import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getSeatsChartChangePreview, SeatsChartChangePreview } from 'services/Seats';
import { PanelDetails, PanelSection, PanelSummary, Pill, Row, Stat } from './SeatsChartChangePreviewPanel.styles';
import { chartChangeCsvQueryKey } from './csv';

interface Props {
	eventId: string;
	newChartKey: string;
	onLoadingChange?: (loading: boolean) => void;
	onErrorChange?: (error: boolean) => void;
}

export const SeatsChartChangePreviewPanel = ({ eventId, newChartKey, onLoadingChange, onErrorChange }: Props) => {
	const { t } = useTranslation();

	const { data, isLoading, isError, error } = useQuery<SeatsChartChangePreview>({
		queryKey: chartChangeCsvQueryKey(eventId, newChartKey),
		queryFn: () => getSeatsChartChangePreview(eventId, newChartKey),
		enabled: !!eventId && !!newChartKey,
		// the preview is a snapshot for this user session — no need to refetch
		// every focus, but do refetch if the user picks a different chart.
		staleTime: 30_000,
		onSuccess: () => onErrorChange?.(false),
		onError: () => onErrorChange?.(true),
		onSettled: () => onLoadingChange?.(false),
	});

	// Mirror loading state back to the parent so it can disable Save.
	if (onLoadingChange) onLoadingChange(isLoading);

	if (isLoading) {
		return (
			<PanelDetails open>
				<PanelSummary>{t('Calculating chart change impact…')}</PanelSummary>
			</PanelDetails>
		);
	}

	if (isError || !data) {
		return (
			<PanelDetails open>
				<PanelSummary>{t('Could not calculate chart change impact')}</PanelSummary>
				<PanelSection>
					<p>{(error as Error)?.message ?? t('Try selecting the chart again.')}</p>
				</PanelSection>
			</PanelDetails>
		);
	}

	const { seats } = data;
	const categories = {
		kept: data.categories?.kept ?? [],
		added: data.categories?.added ?? [],
		removed: data.categories?.removed ?? [],
	};
	const orphaned_seats = data.orphaned_seats ?? [];
	const warnings = data.warnings ?? [];
	const totalChanges =
		categories.added.length + categories.removed.length + seats.will_change_category + seats.will_orphan;

	return (
		<PanelDetails open={totalChanges > 0}>
			<PanelSummary>
				{t('Chart change preview')} — <Pill>{t('{{n}} new', { n: categories.added.length })}</Pill>{' '}
				<Pill>{t('{{n}} removed', { n: categories.removed.length })}</Pill>{' '}
				<Pill>{t('{{n}} kept', { n: categories.kept.length })}</Pill>{' '}
				<Pill tone="warn">{t('{{n}} orphaned seats', { n: seats.will_orphan })}</Pill>
			</PanelSummary>

			<PanelSection>
				<h4>{t('Seats')}</h4>
				<Row>
					<Stat>
						<b>{seats.currently_booked}</b>
						<span>{t('currently booked')}</span>
					</Stat>
					<Stat>
						<b>{seats.will_rebook}</b>
						<span>{t('will rebook on new chart')}</span>
					</Stat>
					<Stat>
						<b>{seats.will_change_category}</b>
						<span>{t('move to different category')}</span>
					</Stat>
					<Stat tone="warn">
						<b>{seats.will_orphan}</b>
						<span>{t('no match — kept as info')}</span>
					</Stat>
				</Row>
			</PanelSection>

			{categories.added.length > 0 && (
				<PanelSection>
					<h4>{t('New categories')}</h4>
					<ul>
						{categories.added.map((c) => (
							<li key={c.key}>
								<b>{c.label}</b> — {t('{{n}} seats', { n: c.new_capacity })}
							</li>
						))}
					</ul>
				</PanelSection>
			)}

			{categories.kept.length > 0 && (
				<PanelSection>
					<h4>{t('Existing categories (capacity will be adjusted)')}</h4>
					<ul>
						{categories.kept.map((c) => (
							<li key={c.ticket_type_id}>
								<b>{c.label}</b>: {c.old_capacity} → {c.new_capacity} {t('seats')}
								{c.sold > 0 && ` (${t('{{n}} sold', { n: c.sold })})`}
								{c.old_key && c.old_key !== c.key && (
									<> — {t('re-keyed {{from}} → {{to}}', { from: c.old_key, to: c.key })}</>
								)}
							</li>
						))}
					</ul>
				</PanelSection>
			)}

			{categories.removed.length > 0 && (
				<PanelSection>
					<h4>{t('Removed categories')}</h4>
					<ul>
						{categories.removed.map((c) => (
							<li key={c.key}>
								<b>{c.label}</b>
								{c.action === 'disable_ticket_type'
									? ` — ${t('{{n}} sold — ticket type will be disabled', { n: c.sold })}`
									: ` — ${t('ticket type will be unlinked from chart')}`}
							</li>
						))}
					</ul>
				</PanelSection>
			)}

			{orphaned_seats.length > 0 && (
				<PanelSection>
					<h4>{t('Orphaned seats')}</h4>
					<p>
						{t(
							'These seats do not exist in the new chart and will not be rebooked. You can reassign them manually after saving.'
						)}
					</p>
					<ul>
						{orphaned_seats.slice(0, 25).map((o) => (
							<li key={o.ticket_id}>
								{t('Seat')} <b>{o.seat_label}</b> — {t('Ticket #{{id}}', { id: o.ticket_id })}
							</li>
						))}
						{orphaned_seats.length > 25 && <li>{t('… and {{n}} more', { n: orphaned_seats.length - 25 })}</li>}
					</ul>
				</PanelSection>
			)}

			{warnings.length > 0 && (
				<PanelSection>
					<h4>{t('Warnings')}</h4>
					<ul>
						{warnings.map((w, i) => (
							<li key={i}>{w}</li>
						))}
					</ul>
				</PanelSection>
			)}
		</PanelDetails>
	);
};
